import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ExamId } from "../../lib/types";
import { CORPUS_SIZE } from "../../data/corpus";
import { useAppStore } from "../../state/app-store-context";

const EXAMS: { value: ExamId; label: string; desc: string; labelClass: string }[] = [
  { value: "hkdse", label: "HKDSE English", desc: `核心 ${CORPUS_SIZE.toLocaleString("en-US")} 詞 · 已按考綱分級`, labelClass: "font-grotesk font-semibold" },
  { value: "ielts", label: "IELTS", desc: "學術詞彙 · 目標 4,500 詞", labelClass: "font-grotesk font-semibold" },
  { value: "toefl", label: "TOEFL", desc: "學術詞彙 · 目標 5,000 詞", labelClass: "font-grotesk font-semibold" },
  { value: "custom", label: "自己導入", desc: "PDF · Word · Excel · 網址", labelClass: "font-bold" },
];

export default function Goal() {
  const navigate = useNavigate();
  const { state, dispatch } = useAppStore();
  const [selected, setSelected] = useState<ExamId>(state.targetExam);
  const [deadline, setDeadline] = useState(state.targetDeadline ?? "");

  const needsDate = selected !== "custom";
  const today = new Date().toISOString().slice(0, 10);
  const dateValid = !needsDate || deadline > today;

  return (
    <div className="min-h-dvh box-border bg-cream flex flex-col pt-[60px] px-[max(22px,4.5vw)] pb-[30px]">
      {/* 3 段进度条:第 1 段 ink,余 track */}
      <div className="flex gap-[5px] mb-[30px]">
        <div className="h-[3px] flex-1 rounded-[2px] bg-ink" />
        <div className="h-[3px] flex-1 rounded-[2px] bg-track" />
        <div className="h-[3px] flex-1 rounded-[2px] bg-track" />
      </div>

      <div className="font-grotesk text-[11px] font-semibold tracking-[.16em] text-ink4">STEP 1 OF 3</div>
      <h2 className="mt-[10px] mb-[6px] text-[27px] font-bold leading-[1.3] text-ink">你在準備哪個考試？</h2>
      <p className="mt-0 mb-[22px] font-grotesk text-[14px] leading-[1.5] text-ink3">
        We'll load the matching word list and work backwards from your exam date.
      </p>

      <div className="flex flex-col gap-[9px]">
        {EXAMS.map((exam) => {
          const active = selected === exam.value;
          return (
            <button
              key={exam.value}
              type="button"
              onClick={() => setSelected(exam.value)}
              className={`flex items-center gap-[13px] px-[16px] py-[15px] rounded-[14px] text-left cursor-pointer ${
                active ? "bg-accent-bg border-[1.5px] border-accent" : "bg-white border border-line"
              }`}
            >
              {active ? (
                <div className="w-5 h-5 shrink-0 rounded-full bg-accent text-white text-[12px] flex items-center justify-center">✓</div>
              ) : (
                <div className="w-5 h-5 shrink-0 rounded-full border-[1.5px] border-line" />
              )}
              <div className="flex-1">
                <div className={`text-[16px] text-ink ${exam.labelClass}`}>{exam.label}</div>
                <div className={`text-[12.5px] mt-[2px] ${active ? "text-accent-deep" : "text-ink4"}`}>{exam.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {needsDate && (
        <div className="mt-[16px]">
          <label htmlFor="exam-date" className="block text-[13.5px] font-semibold text-ink mb-[7px]">
            考試日期
          </label>
          <input
            id="exam-date"
            type="date"
            aria-label="考試日期"
            value={deadline}
            min={today}
            onChange={(e) => setDeadline(e.target.value)}
            className="w-full box-border px-[14px] py-[12px] rounded-[12px] bg-white border border-line text-[15px] text-ink font-grotesk"
          />
          {!dateValid && (
            <p role="alert" className="mt-[7px] mb-0 text-[13px] text-red-600">
              請選擇今天之後的考試日期
            </p>
          )}
        </div>
      )}

      <div className="mt-auto pt-[24px]">
        <button
          type="button"
          disabled={!dateValid}
          onClick={() => {
            dispatch({ type: "setTargetExam", exam: selected });
            dispatch({ type: "setTargetDeadline", deadline: needsDate ? deadline : undefined });
            navigate("/level-test/intro");
          }}
          className={`w-full flex items-center justify-between px-[18px] py-[14px] rounded-full ${
            dateValid ? "bg-ink cursor-pointer" : "bg-ink/40 cursor-not-allowed"
          }`}
        >
          <span className="text-[16px] font-bold text-cream">繼續</span>
          <span className="font-grotesk text-[14px] text-cream/60">Continue →</span>
        </button>
      </div>
    </div>
  );
}
